import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Button } from "@/core/components/ui/button";
import { cn } from "@/core/utils/utils";

const ICON =
  "h-7 w-7 min-w-7 rounded-md p-0 text-secondary-text hover:bg-surface-secondary hover:text-primary-text";

/**
 * Back, where you are, forward: the way through one artifact's changes.
 *
 * It is one control, so it is drawn as one. The three review panes each had
 * their own pair of arrows pinned to the right edge of the control row while
 * the counter they moved sat at the left, and on a wide screen that put a
 * number and the buttons that change it a thousand pixels apart. The position
 * came from `ml-auto`, which is a layout habit rather than a decision.
 *
 * It leads its row, because stepping through what changed is the only thing
 * there is to do on these tabs. What reads second is what you are looking at
 * now: the section, the person, the sheet.
 */
export const ChangeStepper = ({
  label,
  noun,
  onStep,
  className,
}: {
  /** What you are on, e.g. "Edit 1 of 2". Omitted when there is no count. */
  label?: string;
  /** Names the two buttons for a reader who cannot see the row. */
  noun: string;
  onStep: (direction: -1 | 1) => void;
  className?: string;
}) => (
  <div className={cn("flex items-center gap-0.5", className)}>
    <Button
      size="xs"
      variant="ghost"
      className={ICON}
      aria-label={`Previous ${noun}`}
      onClick={() => onStep(-1)}
    >
      <ChevronLeftIcon className="h-4 w-4" />
    </Button>
    {!!label && <span className="px-1 text-secondary-text">{label}</span>}
    <Button
      size="xs"
      variant="ghost"
      className={ICON}
      aria-label={`Next ${noun}`}
      onClick={() => onStep(1)}
    >
      <ChevronRightIcon className="h-4 w-4" />
    </Button>
  </div>
);
